import { useMemo } from 'react'
import { useNewsEvents } from '@/hooks/useNewsEvents'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend } from 'recharts'

const IMPACT_COLORS = {
  high: '#dc2626',
  medium: '#f59e0b',
  low: '#10b981',
  none: '#6b7280'
}

export function AnalyticsDashboard() {
  const { events } = useNewsEvents()

  const stats = useMemo(() => {
    const byImpact = events.reduce((acc, event) => {
      acc[event.impact] = (acc[event.impact] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const byCurrency = events.reduce((acc, event) => {
      acc[event.currency] = (acc[event.currency] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const bySource = events.reduce((acc, event) => {
      const source = event.source || 'unknown'
      acc[source] = (acc[source] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const autoCloseEnabled = events.filter(e => e.auto_close_enabled).length
    const manualEdits = events.filter(e => e.edited_by_admin).length

    return {
      byImpact,
      byCurrency,
      bySource,
      autoCloseEnabled,
      manualEdits,
      total: events.length
    }
  }, [events])

  const impactData = Object.entries(stats.byImpact).map(([name, value]) => ({
    name: name.toUpperCase(),
    value,
    color: IMPACT_COLORS[name as keyof typeof IMPACT_COLORS]
  }))

  const currencyData = Object.entries(stats.byCurrency).map(([name, value]) => ({
    name,
    value
  }))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
        <p className="text-muted-foreground">
          System metrics and performance insights
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Events</CardDescription>
            <CardTitle className="text-4xl">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Auto-Close Enabled</CardDescription>
            <CardTitle className="text-4xl text-emerald-500">
              {stats.autoCloseEnabled}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {stats.total > 0 ? Math.round((stats.autoCloseEnabled / stats.total) * 100) : 0}% of total
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Manual Edits</CardDescription>
            <CardTitle className="text-4xl text-amber-500">
              {stats.manualEdits}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {stats.total > 0 ? Math.round((stats.manualEdits / stats.total) * 100) : 0}% modified
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Currencies Tracked</CardDescription>
            <CardTitle className="text-4xl">{Object.keys(stats.byCurrency).length}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Impact Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Impact Distribution</CardTitle>
            <CardDescription>Events by impact level</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={impactData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {impactData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {impactData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm">
                    {item.name}: {item.value}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Currency Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Currency Distribution</CardTitle>
            <CardDescription>Events per currency</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={currencyData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#3b82f6" name="Events" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 flex gap-2">
              {currencyData.map((item) => (
                <Badge key={item.name} variant="outline">
                  {item.name}: {item.value}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sources */}
      <Card>
        <CardHeader>
          <CardTitle>Data Sources</CardTitle>
          <CardDescription>Events by source</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(stats.bySource).map(([source, count]) => (
              <div key={source} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{source}</Badge>
                  <span className="text-sm text-muted-foreground">
                    {count} events
                  </span>
                </div>
                <div className="h-2 w-64 rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary"
                    style={{ width: `${(count / stats.total) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
